import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactAvatarEditor from 'react-avatar-editor';
import store from 'store';
import { Image } from 'rebass';
import { imageUrlToBlob, renderPositionForVGBImage, scalePosition } from './ImageEditor/helper';
import { capCanvasDims } from 'collections/book-creation/helper';
import getBestImageVersion from 'utils/image-versions';

const timeArray = [];
function ImageNode({
  zoom,
  shouldPlay,
  dispatch,
  nodeInRange,
  onPause,
  onPlay,
  height,
  width,
  isBuffering,
  setBuffering,
  clipDetail,
  canvasImageDetails = {}
}) {
  const [newImageObj, setNewImageObj] = useState(null);

  const rotation = (clipDetail && clipDetail['rotation-angle']) || 0;
  const imageDetails = Object.keys(canvasImageDetails).length > 0 ? canvasImageDetails : clipDetail;
  const editorDims = capCanvasDims(imageDetails?.width, imageDetails?.height);
  const src = getBestImageVersion(imageDetails).url

  useEffect(() => {
    if (isBuffering) setBuffering(false);
  }, []);

  const handleChange = async (url) => {
    try {
      const blob = await imageUrlToBlob(url);
      setNewImageObj(URL.createObjectURL(blob));
    } catch (err) {
      setNewImageObj(imageDetails?.['asset-url']);
    }
  };

  useEffect(() => {
    handleChange(src);
  }, [src]);

  const prevTimeRef = useRef();
  const rafRef = useRef();

  const animateTime = (time) => {
    if (
      prevTimeRef.current &&
      (store.get('PreviewClipPlaying') || store.get('ClipPlaying')) &&
      shouldPlay &&
      store.get('nodeInRange') &&
      time > prevTimeRef.current
    ) {
      const changeTime = time - prevTimeRef.current;
      if (!timeArray.includes(time)) {
        timeArray.push(time);
        const delta = changeTime / 1000;
        if (store.get('currentSeconds')) {
          store.remove('currentSeconds');
        }
        if (store.get('PreviewClipPlaying') || store.get('ClipPlaying'))
          dispatch({ type: 'addSeconds', delta });
      }
    }
    if (store.get('PreviewClipPlaying') || (store.get('ClipPlaying') && store.get('nodeInRange'))) {
      prevTimeRef.current = time;
      rafRef.current = requestAnimationFrame(animateTime);
    }
  };

  // video nodes use callbacks for these
  useEffect(() => {
    if (!shouldPlay && prevTimeRef.current) {
      onPause();
      cancelAnimationFrame(rafRef.current);
      prevTimeRef.current = undefined;
    }
    if (shouldPlay && nodeInRange) {
      rafRef.current = requestAnimationFrame(animateTime);
      onPlay();
    }
    if (nodeInRange) {
      store.set('nodeInRange', true);
    }
    return () => {
      store.remove('nodeInRange');
      cancelAnimationFrame(rafRef.current);
      prevTimeRef.current = undefined;
    };
  }, [onPause, onPlay, shouldPlay, nodeInRange]);

  if (!src) return null

  if (Object.entries(imageDetails)
    .some(([key, value]) =>
      ["crop-left", "crop-top", "crop-width", "crop-height", "left", "top"].includes(key) &&
      value !== 0
    ))
    return <ReactAvatarEditor
      id={src}
      image={newImageObj}
      height={editorDims?.height}
      width={editorDims?.width}
      color={[250, 250, 250, 0]}
      scale={imageDetails?.zoom === 0 ? 1 : (+scalePosition(imageDetails?.zoom) * 3) / 100}
      position={renderPositionForVGBImage({
        x: imageDetails?.left,
        y: imageDetails?.top,
      })}
      crossOrigin="anonymous"
      style={{
        minWidth: "100%",
        width: "100%",
        height: "100%",
        objectFit: 'cover',
      }}
    />

  return (<Image
    sx={{
      objectFit: 'contain',
      gridArea: '1 / 1 / 2 / 2 ',
      objectPosition: 'center',
      transform: `rotate(${rotation}deg)`,
    }}
    height={height}
    width={width}
    src={src}
  />
  );
}

ImageNode.propTypes = {
  id: PropTypes.string.isRequired,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ImageNode.defaultProps = {
  height: 'inherit',
  width: 'inherit',
};

export default ImageNode;
